import { Model } from 'dva'
import { OntologiesService, OrganisationsService } from '../../../Service'
import { IsObject } from '../../../Utils/helpers'
import { FailureHandler } from 'Utils/ApiHelper'
import { Ontology } from '../types'

const Ontologies: Model = {
  namespace: 'ontologies',
  state: {
    list: [],
  },
  effects: {
    *list(
      {
        useCache = true,
        additionalParams = {},
        setLoading = () => {},
        onSuccess = () => {},
        onFailure = () => {},
      },
      { call, put, select }
    ): any {
      setLoading(true)
      try {
        let ontologies = yield select((state: any) => state.ontologies.list)
        if (!(ontologies.length > 0 && useCache)) {
          const flowUuid = yield select((state: any) => {
            return state.flowLayout.flow.uuid
          })
          const flowConfig = yield select((state: any) => {
            return state.flowConfigs.flowConfig?.id
          })
          if (additionalParams?.forCurrentFlowConfig === true && flowConfig !== null) {
            ontologies = yield call(OrganisationsService.organisationsFlowsOntologiesList, {
              organisationUuid: additionalParams?.organisationUuid,
              flowUuid,
              flowConfig: [flowConfig],
            })
          } else if (additionalParams?.forCurrentFlowConfig === false) {
            ontologies = yield call(OntologiesService.ontologiesList, {
              flowConfigIsnull: [true],
            })
          }
          yield put({ type: 'modifyList', payload: ontologies })
        }
        onSuccess(ontologies)
      } catch (err: any) {
        FailureHandler('Error fetching TTS Providers', err, onFailure)
      }
      setLoading(false)
    },
    *retrieve(
      {
        id,
        setLoading = () => {},
        onSuccess = () => {},
        onFailure = () => {},
        additionalParams = {},
      },
      { call, put, select }
    ): any {
      setLoading(true)
      try {
        let ontology = yield select((state: any) =>
          state.ontologies.list.find((ontology: Ontology) => ontology.id === id)
        )
        if (!ontology) {
          const flowUuid = yield select((state: any) => {
            return state.flowLayout.flow.uuid
          })
          const flowConfig = yield select((state: any) => state.flowConfigs?.flowConfig?.id)
          if (additionalParams?.forCurrentFlowConfig === true) {
            ontology = yield call(OrganisationsService.organisationsFlowsOntologiesRetrieve, {
              organisationUuid: additionalParams?.organisationUuid,
              flowUuid,
              id: id,
              flowConfig: [flowConfig],
            })
          }
          ontology = yield call(OntologiesService.ontologiesRetrieve, {
            id: id,
            flowConfig: [flowConfig],
          })
        }
        yield put({ type: 'modifyList', payload: ontology })
        onSuccess(ontology)
      } catch (err) {
        FailureHandler('Could not find the TTS Provider!', err, onFailure)
      }
      setLoading(false)
    },
    *create(
      {
        data = {},
        additionalParams = {},
        setLoading = () => {},
        onSuccess = () => {},
        onFailure = () => {},
      },
      { call, put, select }
    ): any {
      setLoading(true)
      try {
        const flowConfig = yield select((state: any) => state.flowConfigs?.flowConfig?.id)
        const flowUuid = yield select((state: any) => {
          return state.flowLayout.flow.uuid
        })
        let response
        if (additionalParams?.forCurrentFlowConfig === true) {
          response = yield call(OrganisationsService.organisationsFlowsOntologiesCreate, {
            organisationUuid: additionalParams?.organisationUuid,
            flowUuid,
            requestBody: { ...data, flow_config: flowConfig },
          })
        } else {
          response = yield call(OntologiesService.ontologiesCreate, {
            requestBody: { ...data },
          })
        }
        yield put({ type: 'modifyList', payload: response })
        onSuccess(response)
      } catch (err) {
        FailureHandler('Error adding a TTS Provider!', err, onFailure)
      }
      setLoading(false)
    },
    *partialUpdate(
      {
        id,
        data,
        additionalParams = {},
        setLoading = () => {},
        onSuccess = () => {},
        onFailure = () => {},
      },
      { call, put, select }
    ): any {
      setLoading(true)
      try {
        let response = null
        if (additionalParams?.forCurrentFlowConfig === true) {
          const flowUuid = yield select((state: any) => {
            return state.flowLayout.flow.uuid
          })
          response = yield call(OrganisationsService.organisationsFlowsOntologiesPartialUpdate, {
            organisationUuid: additionalParams?.organisationUuid,
            flowUuid,
            id: id,
            requestBody: data,
          })
        } else {
          response = yield call(OntologiesService.ontologiesPartialUpdate, {
            id: id,
            requestBody: data,
          })
        }
        yield put({ type: 'modifyList', payload: response })
        onSuccess(response)
      } catch (err) {
        FailureHandler('Error editing a TTS Provider!', err, onFailure)
      }
      setLoading(false)
    },
    *destroy(
      {
        id,
        setLoading = () => {},
        onSuccess = () => {},
        onFailure = () => {},
        additionalParams = {},
      },
      { call, put, select }
    ): any {
      setLoading(true)
      try {
        if (additionalParams?.forCurrentFlowConfig === true) {
          const flowUuid = yield select((state: any) => {
            return state.flowLayout.flow.uuid
          })
          yield call(OrganisationsService.organisationsFlowsOntologiesDestroy, {
            organisationUuid: additionalParams?.organisationUuid,
            flowUuid: flowUuid,
            id,
          })
        } else {
          yield call(OntologiesService.ontologiesDestroy, { id: id })
        }
        yield put({ type: 'modifyList', payload: id })
        onSuccess()
      } catch (err) {
        FailureHandler('Error deleting a ontology', err, onFailure)
      }
      setLoading(false)
    },
  },
  reducers: {
    modifyList(state, action) {
      // clone the list
      let list = [...state.list]
      if (IsObject(action.payload)) {
        // if the payload is an object, it's a single object, so add it to the list
        list = list.filter((l: Ontology) => l.id !== action.payload.id)
        list.push(action.payload)
      } else if (Array.isArray(action.payload)) {
        // if the payload is an array, it's a list of objects, so add them to the list
        list = action.payload
      } else if (Number.isInteger(action.payload)) {
        // if the payload is an integer, it's an id sent in delete operations, so remove it from the list
        list = list.filter((l: Ontology) => l.id !== action.payload)
      }
      return {
        ...state,
        list: list,
      }
    },
    setList(state, action) {
      // Purge list from redux and set a new list passed in payload
      return {
        ...state,
        list: action.payload,
      }
    },
    resetOntologies(state, action) {
      return {
        ...state,
        list: [],
      }
    },
  },
}

export default Ontologies
